/**
 * GWP - Gift With Purchase (v2)
 * Hawaianas Club
 *
 * Refactor v2:
 * - Sin polling: detección automática de cambios interceptando fetch y XMLHttpRequest
 *   sobre /cart/add.js, /cart/change.js, /cart/update.js y /cart/clear.js.
 * - removeUndeservedGifts() calcula sobrantes por CANTIDAD (item.quantity), no por
 *   cantidad de líneas. Soporta remoción parcial, total y múltiples líneas del mismo regalo.
 * - Mutex (checkRunning / checkPending): nunca corren dos checkCart en simultáneo.
 * - Las mutaciones propias del script no re-disparan el interceptor (sin loops).
 * - En /cart, si se eliminaron regalos huérfanos, recarga la página UNA sola vez.
 * - Hash del carrito incluye `key` para detectar cambios de line items.
 * - fetchCart() usa cache: "no-store".
 *
 * CONFIGURACIÓN — editá solo esta sección si cambian los productos
 */

const GWP_CONFIG = {

  triggers: {
    buzo:    ['buzo'],
    remera:  ['remera'],
    matera:  ['matera'],
    yerbera: ['yerbera'],
    totebag: ['tote bag'],
  },

  gifts: {
    medias: [
      {
        id: 8843161731225,
        title: 'Medias Blancas',
        displayName: 'Medias Urban Blanca',
        image: 'https://cdn.shopify.com/s/files/1/0676/4265/4873/files/MediasUrbanHawaianasClub.png?v=1780509124',
        variants: [
          { id: 48401678532761, title: '35 al 39' },
          { id: 48401678565529, title: '40 al 44' },
        ]
      },
      {
        id: 8843165859993,
        title: 'Medias Negras',
        displayName: 'Medias Urban Negra',
        image: 'https://cdn.shopify.com/s/files/1/0676/4265/4873/files/MediasUrbanHawaianasClubNegra.png?v=1780506615',
        variants: [
          { id: 48401685545113, title: '35 al 39' },
          { id: 48401685577881, title: '40 al 44' },
        ]
      }
    ],
    pantuflas: [
      {
        id: 8843343462553,
        title: 'Pantuflas Home Elevate',
        displayName: 'Pantuflas Hawaianas Home Elevate',
        image: 'https://cdn.shopify.com/s/files/1/0676/4265/4873/files/PantuflasHawaianasHomeElevate.png?v=1779124338',
        variants: [
          { id: 48401898995865, title: '35/36' },
          { id: 48401899028633, title: '37/38' },
          { id: 48401899061401, title: '39/40' },
          { id: 48401903059097, title: '41/43' },
          { id: 48401903091865, title: '43/44' },
          { id: 48401903124633, title: '45/46' },
        ]
      }
    ]
  },

  giftProperty: '_gwp_gift',
};

// ============================================
// ESTADO DE SESIÓN
// ============================================

const GWP_SESSION_KEY = 'gwp_session';
const GWP_RELOAD_FLAG = 'gwp_cart_page_reloaded'; // evita recargas infinitas en /cart

function getSession() {
  try {
    return JSON.parse(sessionStorage.getItem(GWP_SESSION_KEY)) || {};
  } catch { return {}; }
}

function saveSession(data) {
  try {
    sessionStorage.setItem(GWP_SESSION_KEY, JSON.stringify(data));
  } catch { /* sessionStorage no disponible: seguimos sin persistencia */ }
}

// ============================================
// HELPERS
// ============================================

function titleMatches(title, keywords) {
  const t = title.toLowerCase();
  return keywords.some(k => t.includes(k));
}

function isGiftItem(item) {
  return Boolean(item.properties && item.properties[GWP_CONFIG.giftProperty]);
}

function giftTypeOf(item) {
  return item.properties ? item.properties[GWP_CONFIG.giftProperty] : undefined;
}

function sumQuantity(items) {
  return items.reduce((sum, i) => sum + i.quantity, 0);
}

function isCartPage() {
  return window.location.pathname.replace(/\/+$/, '') === '/cart';
}

function isModalOpen() {
  const overlay = document.getElementById('gwp-overlay');
  return Boolean(overlay && overlay.classList.contains('gwp-open'));
}

/**
 * Hash del carrito. Incluye `key` (además de variante, cantidad y propiedades)
 * para detectar cambios de line items aunque variante y cantidad coincidan.
 */
function computeCartHash(items) {
  return JSON.stringify(
    items.map(i => ({
      key: i.key,
      id: i.variant_id,
      qty: i.quantity,
      props: i.properties,
    }))
  );
}

// ============================================
// RED — REQUESTS INTERNOS (no disparan el interceptor)
// ============================================

// Contador de requests propios en vuelo. Mientras sea > 0, el interceptor
// ignora las mutaciones del carrito (son nuestras) y no re-dispara checkCart.
let internalRequestDepth = 0;

async function internalFetch(url, options) {
  internalRequestDepth++;
  try {
    return await window.fetch(url, options);
  } finally {
    internalRequestDepth--;
  }
}

async function fetchCart() {
  const res = await internalFetch('/cart.js', { cache: 'no-store' });
  if (!res.ok) throw new Error(`No se pudo leer el carrito (HTTP ${res.status})`);
  return res.json();
}

async function changeLineQuantity(lineKey, quantity) {
  const res = await internalFetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id: lineKey, quantity }),
  });
  if (!res.ok) throw new Error(`Error en /cart/change.js (HTTP ${res.status})`);
  return res.json();
}

// ============================================
// CÁLCULO DE MERECIDOS (compartido)
// ============================================

function calcularMerecidos(cartItems) {
  const eligible = cartItems.filter(i => !isGiftItem(i));

  let buzos = 0, remeras = 0, materas = 0, yerberas = 0, totebags = 0;
  eligible.forEach(item => {
    const qty = item.quantity;
    if (titleMatches(item.title, GWP_CONFIG.triggers.buzo))    buzos    += qty;
    if (titleMatches(item.title, GWP_CONFIG.triggers.remera))  remeras  += qty;
    if (titleMatches(item.title, GWP_CONFIG.triggers.matera))  materas  += qty;
    if (titleMatches(item.title, GWP_CONFIG.triggers.yerbera)) yerberas += qty;
    if (titleMatches(item.title, GWP_CONFIG.triggers.totebag)) totebags += qty;
  });

  const pairs         = Math.min(buzos, remeras);
  const buzosLibres   = buzos - pairs;
  const remerasLibres = remeras - pairs;

  return {
    pantuflasMerecidas: pairs,
    mediasMerecidas: buzosLibres + remerasLibres + materas + yerberas + totebags,
  };
}

// ============================================
// LÓGICA DE REGLAS
// ============================================

function evaluateCart(cartItems) {
  const session = getSession();

  const giftsInCart = cartItems.filter(isGiftItem);
  const pantuflaEnCarrito = sumQuantity(giftsInCart.filter(g => giftTypeOf(g) === 'pantuflas'));
  const mediasEnCarrito   = sumQuantity(giftsInCart.filter(g => giftTypeOf(g) === 'medias'));

  const { pantuflasMerecidas, mediasMerecidas } = calcularMerecidos(cartItems);

  const pantuflasDismissed = Math.min(
    session.dismissed_pantuflas || 0,
    Math.max(0, pantuflasMerecidas - pantuflaEnCarrito)
  );
  const mediasDismissed = Math.min(
    session.dismissed_medias || 0,
    Math.max(0, mediasMerecidas - mediasEnCarrito)
  );

  const pantuflasPending = pantuflasMerecidas - pantuflaEnCarrito - pantuflasDismissed;
  const mediasPending    = mediasMerecidas    - mediasEnCarrito   - mediasDismissed;

  const pendingGifts = [];
  for (let i = 0; i < pantuflasPending; i++) {
    pendingGifts.push({ type: 'pantuflas', reason: 'Compraste un buzo + remera' });
  }
  for (let i = 0; i < mediasPending; i++) {
    pendingGifts.push({ type: 'medias', reason: 'Compraste un producto Hawaianas Club' });
  }

  return pendingGifts;
}

// ============================================
// LIMPIEZA DE REGALOS NO MERECIDOS
// ============================================

/**
 * Reduce líneas de regalo hasta eliminar `excessQty` unidades.
 * Soporta remoción parcial (baja la cantidad), total (quantity: 0)
 * y varias líneas del mismo regalo.
 */
async function removeExcessQuantity(giftLines, excessQty) {
  let remaining = excessQty;

  for (const line of giftLines) {
    if (remaining <= 0) break;

    const toRemove = Math.min(line.quantity, remaining);
    const newQuantity = line.quantity - toRemove;

    console.log('[GWP] Eliminando regalo sobrante:', line.title, '| key:', line.key, '| qty', line.quantity, '→', newQuantity);
    await changeLineQuantity(line.key, newQuantity);

    remaining -= toRemove;
  }
}

/**
 * Elimina del carrito los regalos que ya no se merecen.
 * El sobrante se calcula por CANTIDAD total (suma de item.quantity),
 * no por cantidad de líneas.
 * Devuelve true si eliminó algo.
 */
async function removeUndeservedGifts(cartItems) {
  const giftsInCart = cartItems.filter(isGiftItem);
  if (giftsInCart.length === 0) return false;

  const { pantuflasMerecidas, mediasMerecidas } = calcularMerecidos(cartItems);

  const pantuflaLines = giftsInCart.filter(g => giftTypeOf(g) === 'pantuflas');
  const mediasLines   = giftsInCart.filter(g => giftTypeOf(g) === 'medias');

  const pantuflasSobrantes = sumQuantity(pantuflaLines) - pantuflasMerecidas;
  const mediasSobrantes    = sumQuantity(mediasLines)   - mediasMerecidas;

  if (pantuflasSobrantes <= 0 && mediasSobrantes <= 0) return false;

  if (pantuflasSobrantes > 0) await removeExcessQuantity(pantuflaLines, pantuflasSobrantes);
  if (mediasSobrantes > 0)    await removeExcessQuantity(mediasLines, mediasSobrantes);

  return true;
}

// ============================================
// HELPER — REFRESCAR DRAWER
// ============================================

function refreshDrawer() {
  // Eventos genéricos usados por muchos themes (compatibilidad existente)
  document.dispatchEvent(new CustomEvent('cart:updated'));
  document.dispatchEvent(new CustomEvent('theme:cart:open'));
  window.dispatchEvent(new CustomEvent('cart:refresh'));
  ['slide-cart:refresh', 'slideCart:refresh', 'cart:reload', 'ajaxCart:refresh', 'cart:change', 'cart:build', 'cartdrawer:refresh'].forEach(e => {
    document.dispatchEvent(new CustomEvent(e));
    window.dispatchEvent(new CustomEvent(e));
  });

  // Themes modernos (Dawn y derivados): custom elements con hooks de actualización
  ['cart-drawer-items', 'cart-items', 'cart-drawer', 'cart-notification'].forEach(selector => {
    const el = document.querySelector(selector);
    if (el && typeof el.onCartUpdate === 'function') {
      try { el.onCartUpdate(); } catch { /* el theme decide */ }
    }
  });

  // Pub/sub de Dawn (publish global), si existe
  if (typeof window.publish === 'function') {
    try { window.publish('cart-update', { source: 'gwp' }); } catch { /* no-op */ }
  }
}

// ============================================
// UI — MODAL
// ============================================

let currentGiftQueue = [];
let currentGiftIndex = 0;
let selectedGiftProduct = null;
let selectedVariantId = null;

function openGiftModal(giftQueue) {
  currentGiftQueue = giftQueue;
  currentGiftIndex = 0;
  renderGiftStep();
  document.getElementById('gwp-overlay').classList.add('gwp-open');
  document.getElementById('gwp-reminder').classList.remove('gwp-reminder-visible');
  document.body.style.overflow = 'hidden';
}

function closeGiftModal(dismissed) {
  document.getElementById('gwp-overlay').classList.remove('gwp-open');
  document.body.style.overflow = '';

  if (!dismissed || currentGiftQueue.length === 0) return;

  const session = getSession();
  const remaining = currentGiftQueue.slice(currentGiftIndex);
  const pantuflasDismissedNow = remaining.filter(g => g.type === 'pantuflas').length;
  const mediasDismissedNow    = remaining.filter(g => g.type === 'medias').length;
  session.dismissed_pantuflas = (session.dismissed_pantuflas || 0) + pantuflasDismissedNow;
  session.dismissed_medias    = (session.dismissed_medias    || 0) + mediasDismissedNow;
  saveSession(session);
  showReminder();
}

function renderGiftStep() {
  if (currentGiftIndex >= currentGiftQueue.length) {
    closeGiftModal(false);
    return;
  }

  const gift = currentGiftQueue[currentGiftIndex];
  const giftOptions = GWP_CONFIG.gifts[gift.type];

  document.getElementById('gwp-modal-subtitle').textContent =
    `${gift.reason} — elegí tu regalo (${currentGiftIndex + 1} de ${currentGiftQueue.length}):`;

  selectedGiftProduct = null;
  selectedVariantId = null;
  document.getElementById('gwp-confirm-btn').disabled = true;
  document.getElementById('gwp-variant-section').style.display = 'none';

  const grid = document.getElementById('gwp-gifts-grid');
  grid.innerHTML = '';

  giftOptions.forEach(product => {
    const card = document.createElement('div');
    card.className = 'gwp-gift-card';
    card.dataset.productId = product.id;
    card.innerHTML = `
      <img src="${product.image}" alt="${product.displayName}" loading="lazy">
      <div class="gwp-gift-name">${product.displayName}</div>
      <div class="gwp-gift-badge">GRATIS</div>
    `;
    card.addEventListener('click', () => selectGiftProduct(product, card));
    grid.appendChild(card);
  });
}

function selectGiftProduct(product, cardEl) {
  document.querySelectorAll('.gwp-gift-card').forEach(c => c.classList.remove('gwp-selected'));
  cardEl.classList.add('gwp-selected');
  selectedGiftProduct = product;
  selectedVariantId = null;
  document.getElementById('gwp-confirm-btn').disabled = true;

  const variantSection = document.getElementById('gwp-variant-section');
  const variantButtons = document.getElementById('gwp-variant-buttons');
  variantSection.style.display = 'block';
  variantButtons.innerHTML = '';

  product.variants.forEach(variant => {
    const btn = document.createElement('button');
    btn.className = 'gwp-variant-btn';
    btn.textContent = variant.title;
    btn.dataset.variantId = variant.id;
    btn.addEventListener('click', () => selectVariant(variant.id, btn));
    variantButtons.appendChild(btn);
  });
}

function selectVariant(variantId, btnEl) {
  document.querySelectorAll('.gwp-variant-btn').forEach(b => b.classList.remove('gwp-variant-selected'));
  btnEl.classList.add('gwp-variant-selected');
  selectedVariantId = variantId;
  document.getElementById('gwp-confirm-btn').disabled = false;
}

async function confirmGift() {
  if (!selectedGiftProduct || !selectedVariantId) return;

  const btn = document.getElementById('gwp-confirm-btn');
  btn.disabled = true;
  btn.textContent = 'Agregando...';

  const giftType = currentGiftQueue[currentGiftIndex].type;

  try {
    // Request interno: el interceptor lo ignora (no genera loops)
    const response = await internalFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: selectedVariantId,
        quantity: 1,
        properties: {
          [GWP_CONFIG.giftProperty]: giftType,
          '_gwp_display': '🎁 REGALO',
        }
      })
    });

    if (!response.ok) throw new Error('Error al agregar al carrito');

    refreshDrawer();
    showToast(selectedGiftProduct);

    currentGiftIndex++;
    if (currentGiftIndex < currentGiftQueue.length) {
      btn.textContent = 'Agregar regalo al carrito';
      btn.disabled = true;
      renderGiftStep();
    } else {
      closeGiftModal(false);
    }

  } catch (err) {
    console.error('[GWP] Error:', err);
    btn.disabled = false;
    btn.textContent = 'Agregar regalo al carrito';
    alert('Hubo un error al agregar el regalo. Por favor intentá de nuevo.');
  }
}

// ============================================
// UI — TOAST
// ============================================

let toastTimeout = null;

function showToast(product) {
  const toast = document.getElementById('gwp-toast');
  document.getElementById('gwp-toast-img').src = product.image;
  document.getElementById('gwp-toast-name').textContent = product.displayName;
  toast.classList.add('gwp-toast-visible');

  // Evita que toasts consecutivos se pisen entre sí
  clearTimeout(toastTimeout);
  toastTimeout = setTimeout(() => {
    toast.classList.remove('gwp-toast-visible');
  }, 5000);
}

// ============================================
// UI — REMINDER
// ============================================

function showReminder() {
  document.getElementById('gwp-reminder').classList.add('gwp-reminder-visible');
}

function hideReminder() {
  document.getElementById('gwp-reminder').classList.remove('gwp-reminder-visible');
}

// ============================================
// CHECK DEL CARRITO (sin polling)
// ============================================

let lastCartHash = null;
let checkRunning = false;
let checkPending = false;

/**
 * Mutex: si ya hay un check corriendo, marca uno pendiente y sale.
 * Al terminar, si quedó algo pendiente, corre una sola vez más.
 * Nunca hay dos checks en simultáneo.
 */
async function checkCart() {
  if (checkRunning) {
    checkPending = true;
    return;
  }

  checkRunning = true;
  try {
    await runCartCheck();
  } catch (err) {
    console.error('[GWP] Error al leer carrito:', err);
  } finally {
    checkRunning = false;
    if (checkPending) {
      checkPending = false;
      checkCart();
    }
  }
}

/**
 * Flujo:
 * 1. leer carrito
 * 2. eliminar regalos sobrantes
 * 3. si eliminó → volver a leer carrito, refrescar drawer
 *    y, si estamos en /cart, recargar la página SOLO una vez
 * 4. evaluar nuevos regalos
 * 5. mostrar modal si corresponde
 */
async function runCartCheck() {
  if (isModalOpen()) return;

  const cart = await fetchCart();
  const hash = computeCartHash(cart.items);

  if (hash === lastCartHash) return;
  lastCartHash = hash;

  const removed = await removeUndeservedGifts(cart.items);

  let currentItems = cart.items;
  if (removed) {
    const updatedCart = await fetchCart();
    currentItems = updatedCart.items;
    lastCartHash = computeCartHash(currentItems);
    refreshDrawer();

    // En /cart el HTML es server-rendered: recargamos para que el regalo
    // eliminado desaparezca visualmente. Solo una vez, con flag de sesión.
    if (isCartPage() && reloadCartPageOnce()) return;
  } else {
    // Check limpio: habilita una futura recarga si vuelve a hacer falta
    sessionStorage.removeItem(GWP_RELOAD_FLAG);
  }

  const pending = evaluateCart(currentItems);

  hideReminder();
  if (pending.length > 0) openGiftModal(pending);
}

function reloadCartPageOnce() {
  if (sessionStorage.getItem(GWP_RELOAD_FLAG)) return false;
  sessionStorage.setItem(GWP_RELOAD_FLAG, '1');
  window.location.reload();
  return true;
}

// ============================================
// DETECCIÓN DE CAMBIOS — INTERCEPTORES DE RED
// ============================================

const CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

function isCartMutationUrl(url) {
  if (!url) return false;
  try {
    const path = new URL(url, window.location.origin).pathname;
    return CART_MUTATION_PATHS.some(p => path.startsWith(p));
  } catch {
    return false;
  }
}

let scheduledCheckTimer = null;

// Pequeño debounce: agrupa ráfagas de mutaciones en un solo check
function scheduleCartCheck() {
  clearTimeout(scheduledCheckTimer);
  scheduledCheckTimer = setTimeout(checkCart, 150);
}

function interceptFetch() {
  const originalFetch = window.fetch;

  window.fetch = function (...args) {
    const input = args[0];
    const url = typeof input === 'string' ? input : (input && input.url);

    // Capturado en el momento del llamado: los requests propios del script
    // (internalFetch) tienen internalRequestDepth > 0 y se ignoran.
    const shouldTrack = isCartMutationUrl(url) && internalRequestDepth === 0;

    const promise = originalFetch.apply(this, args);

    if (shouldTrack) {
      promise.then(() => scheduleCartCheck()).catch(() => {});
    }

    return promise;
  };
}

function interceptXHR() {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._gwpIsCartMutation = isCartMutationUrl(url);
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (this._gwpIsCartMutation) {
      this.addEventListener('loadend', () => scheduleCartCheck());
    }
    return originalSend.apply(this, args);
  };
}

// ============================================
// INIT
// ============================================

function reactivateDismissedGifts() {
  hideReminder();
  const session = getSession();
  session.dismissed_pantuflas = 0;
  session.dismissed_medias = 0;
  saveSession(session);
  lastCartHash = null;
  checkCart();
}

function bindUIEvents() {
  document.getElementById('gwp-modal-close-btn').addEventListener('click', () => {
    closeGiftModal(true);
  });

  document.getElementById('gwp-overlay').addEventListener('click', (e) => {
    if (e.target === document.getElementById('gwp-overlay')) {
      closeGiftModal(true);
    }
  });

  document.getElementById('gwp-confirm-btn').addEventListener('click', confirmGift);

  document.getElementById('gwp-toast-close-btn').addEventListener('click', () => {
    document.getElementById('gwp-toast').classList.remove('gwp-toast-visible');
  });

  const reminder = document.getElementById('gwp-reminder');
  reminder.addEventListener('click', reactivateDismissedGifts);
  reminder.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') reactivateDismissedGifts();
  });
}

function initGWP() {
  // Si el markup del GWP no está en la página, no inicializamos (evita errores)
  if (!document.getElementById('gwp-overlay')) {
    console.warn('[GWP] Markup no encontrado: el script no se inicializó.');
    return;
  }

  bindUIEvents();

  // Detección automática de cambios del carrito (reemplaza al polling)
  interceptFetch();
  interceptXHR();

  // Fallback: eventos que muchos themes disparan al actualizar el carrito
  document.addEventListener('cart:updated', scheduleCartCheck);
  document.addEventListener('theme:cart:open', scheduleCartCheck);

  // Check inicial al cargar la página
  checkCart();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGWP);
} else {
  initGWP();
}